import { AUTH_CONSTANTS } from '../../core/auth/constants/auth.constants';

/**
 * Authentication utility functions
 */
export class AuthUtils {
  /**
   * Check if a token has expired based on its expiry time
   */
  static isTokenExpired(expiryTime: string | null): boolean {
    if (!expiryTime) return true;
    return new Date() >= new Date(expiryTime);
  }

  /**
   * Check if current route is a callback route
   */
  static isCallbackRoute(): boolean {
    return location.pathname.includes(AUTH_CONSTANTS.ROUTES.LOGIN_CALLBACK);
  }

  /**
   * Generate token expiry time
   */
  static generateTokenExpiry(hours: number = AUTH_CONSTANTS.TOKEN_EXPIRY_HOURS): string {
    const expiryTime = new Date();
    expiryTime.setHours(expiryTime.getHours() + hours);
    return expiryTime.toISOString();
  }

  /**
   * Create a delay promise for smooth loading transitions
   */
  static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if error is network related
   */
  static isNetworkError(error: any): boolean {
    const errorMessage = error?.message?.toLowerCase() || '';
    return errorMessage.includes(AUTH_CONSTANTS.ERROR_TYPES.NETWORK) ||
           errorMessage.includes(AUTH_CONSTANTS.ERROR_TYPES.FETCH);
  }

  /**
   * Get user display name from user profile
   */
  static getUserDisplayName(userProfile: any): string {
    return userProfile?.firstName || userProfile?.fullName || 'User';
  }

  /**
   * Clear all authentication related storage
   */
  static clearAuthStorage(): void {
    const keys = Object.values(AUTH_CONSTANTS.STORAGE_KEYS);
    keys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Store API token with expiry
   */
  static storeApiToken(token: string, expiryHours: number = AUTH_CONSTANTS.TOKEN_EXPIRY_HOURS): void {
    localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.API_TOKEN, token);
    localStorage.setItem(
      AUTH_CONSTANTS.STORAGE_KEYS.API_TOKEN_EXPIRY,
      this.generateTokenExpiry(expiryHours)
    );
  }

  /**
   * Get stored API token if valid
   */
  static getValidApiToken(): string | null {
    const token = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.API_TOKEN);
    const expiry = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.API_TOKEN_EXPIRY);

    if (!token || this.isTokenExpired(expiry)) {
      return null;
    }

    return token;
  }
}
